// 🍪 SERVICIO DE GESTIÓN DE COOKIES Y CONSENTIMIENTO GDPR/LGPD
class CookieService {
  constructor() {
    this.PIXEL_ID = '1353964655681289';
    this.isPixelLoaded = false;
    this.isPixelActive = false;
    this.consentGiven = false;
    this.API_BASE_URL = 'https://api.nexusai.company/api'; // ✅ Agregar /api
    
    // Inicializar el servicio
    this.init();
  }

  // 🚀 INICIALIZACIÓN DEL SERVICIO
  init() {
    try {
      console.log('🍪 Inicializando CookieService...');
      
      // Verificar si ya hay consentimiento guardado
      const savedConsent = this.getStoredConsent();
      if (savedConsent) {
        console.log('✅ Consentimiento previo encontrado:', savedConsent);
        this.consentGiven = true;
        
        // Activar servicios según el consentimiento previo
        if (savedConsent.marketing) {
          this.activatePixel();
        }
      } else {
        console.log('ℹ️ No hay consentimiento previo, esperando interacción del usuario');
      }
      
    } catch (error) {
      console.error('❌ Error al inicializar CookieService:', error);
    }
  }

  // 🔍 VERIFICAR SI NECESITA MOSTRAR EL BANNER
  needsConsent() {
    const consent = localStorage.getItem('nexusai-consent-given');
    const preferences = localStorage.getItem('nexusai-cookie-preferences');
    
    // Mostrar banner si no hay consentimiento O si las preferencias están corruptas
    if (!consent || !preferences) {
      console.log('🔔 Se necesita mostrar el banner de cookies');
      return true;
    }
    
    try {
      const parsed = JSON.parse(preferences);
      const isValid = parsed && typeof parsed.marketing === 'boolean';
      
      if (!isValid) {
        console.log('⚠️ Preferencias corruptas, se necesita nuevo consentimiento');
        return true;
      }
      
      console.log('✅ Consentimiento válido encontrado, no mostrar banner');
      return false;
    } catch (error) {
      console.error('❌ Error al verificar consentimiento:', error);
      return true;
    }
  }

  // 📦 OBTENER CONSENTIMIENTO ALMACENADO
  getStoredConsent() {
    try {
      const preferences = localStorage.getItem('nexusai-cookie-preferences');
      const consentGiven = localStorage.getItem('nexusai-consent-given');
      
      if (!preferences || !consentGiven) {
        return null;
      }
      
      return JSON.parse(preferences);
    } catch (error) {
      console.error('❌ Error al obtener consentimiento almacenado:', error);
      return null;
    }
  }

  // 💾 GUARDAR PREFERENCIAS (FUNCIÓN PRINCIPAL)
  // 💾 GUARDAR PREFERENCIAS (FUNCIÓN PRINCIPAL)
async savePreferences(acceptMarketing, customSettings = null) {
  // ✅ Prevenir ejecuciones duplicadas
  if (this.isSaving) {
    console.log('⏳ Ya se está guardando, ignorando petición duplicada');
    return { 
      preferences: this.getStoredConsent(), 
      backendSaved: false, 
      error: 'Already saving',
      userId: localStorage.getItem('nexusai-cookie-user-id')
    };
  }

  this.isSaving = true; // ✅ Marcar como ocupado

  try {
    const preferences = {
      marketing: acceptMarketing,
      essential: true, // Siempre true
      analytics: customSettings?.analytics || acceptMarketing,
      personalization: customSettings?.personalization || acceptMarketing,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };

    console.log('💾 Guardando preferencias de cookies:', preferences);

    // 💽 GUARDAR EN LOCALSTORAGE PRIMERO
    localStorage.setItem('nexusai-cookie-preferences', JSON.stringify(preferences));
    localStorage.setItem('nexusai-consent-given', 'true');
    localStorage.setItem('nexusai-consent-timestamp', new Date().toISOString());
    
    // 🎯 ACTIVAR/DESACTIVAR FACEBOOK PIXEL
    if (acceptMarketing) {
      this.activatePixel();
    } else {
      this.deactivatePixel();
    }

    // Marcar como consentimiento dado
    this.consentGiven = true;
    
    // 🌐 ENVIAR AL BACKEND (asíncrono, no bloquea la UI)
    const backendResult = await this.sendConsentToBackend(preferences);
    
    console.log('✅ Preferencias guardadas exitosamente');
    
    return {
      preferences,
      backendSaved: backendResult?.success || false,
      userId: backendResult?.user_id || null,
      wasLoggedUser: backendResult?.was_logged || false
    };
    
  } catch (error) {
    console.error('❌ Error al guardar preferencias de cookies:', error);
    return {
      preferences: null,
      backendSaved: false,
      error: error.message,
      userId: null
    };
  } finally {
    // ✅ Liberar el bloqueo después de un pequeño delay para evitar race conditions
    setTimeout(() => {
      this.isSaving = false;
    }, 500);
  }
}

  // 🌐 ENVIAR CONSENTIMIENTO AL BACKEND
  async sendConsentToBackend(preferences) {
    try {
      const sessionId = this.getOrCreateSessionId();
      
      const consentData = {
        marketing_accepted: preferences.marketing,
        analytics_accepted: preferences.analytics,
        personalization_accepted: preferences.personalization,
        consent_method: preferences.marketing ? 'accept_all' : 'essential_only',
        consent_source: 'banner',
        session_id: sessionId,
        page_url: window.location.href,
        referrer_url: document.referrer || null
      };

      // 🔑 INCLUIR TOKEN SI EXISTE (para usuarios logueados)
      const token = localStorage.getItem('nexusai-token');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        console.log('👤 Enviando consentimiento como usuario logueado');
      } else {
        console.log('👤 Enviando consentimiento como usuario anónimo');
      }

      console.log('🌐 Enviando consentimiento al backend:', {
        marketing: consentData.marketing_accepted,
        method: consentData.consent_method,
        source: consentData.consent_source
      });

      const response = await fetch(`${this.API_BASE_URL}/cookie-consent`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(consentData)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        console.log('✅ Consentimiento enviado al servidor exitosamente:', {
          user_id: result.data.user_id,
          was_logged: result.data.was_logged_user,
          method: result.data.consent_method
        });
        
        // Guardar el user_id que retorna el servidor
        localStorage.setItem('nexusai-cookie-user-id', result.data.user_id);
        
        return {
          success: true,
          user_id: result.data.user_id,
          was_logged: result.data.was_logged_user
        };
      } else {
        console.error('❌ Error en respuesta del servidor:', result.message);
        return { success: false, error: result.message };
      }

    } catch (error) {
      console.error('❌ Error de conexión al enviar consentimiento:', error);
      // No bloquear la experiencia del usuario si falla el backend
      // El consentimiento ya está guardado localmente
      return { success: false, error: error.message };
    }
  }

  // 🆔 OBTENER O CREAR SESSION ID
  getOrCreateSessionId() {
    let sessionId = localStorage.getItem('nexusai-session-id');
    if (!sessionId) {
      sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('nexusai-session-id', sessionId);
      console.log('🆔 Nuevo session ID creado:', sessionId);
    }
    return sessionId;
  }

  // 👤 OBTENER USUARIO ACTUAL (si existe)
  getCurrentUser() {
    try {
      const userStr = localStorage.getItem('nexusai-current-user');
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.error('❌ Error al obtener usuario actual:', error);
      return null;
    }
  }

  // 🎯 ACTIVAR FACEBOOK PIXEL
  activatePixel() {
    try {
      if (this.isPixelActive) {
        console.log('ℹ️ Facebook Pixel ya está activo');
        return;
      }

      console.log('🎯 Activando Facebook Pixel...');

      // Cargar script de Facebook Pixel si no está cargado
      if (!this.isPixelLoaded) {
        this.loadPixelScript();
      }

      // Inicializar y activar
      if (window.fbq) {
        window.fbq('init', this.PIXEL_ID);
        window.fbq('track', 'PageView');
        this.isPixelActive = true;
        console.log('✅ Facebook Pixel activado exitosamente');
      } else {
        console.warn('⚠️ Facebook Pixel no disponible aún, reintentando...');
        setTimeout(() => this.activatePixel(), 1000);
      }

    } catch (error) {
      console.error('❌ Error al activar Facebook Pixel:', error);
    }
  }

  // 🚫 DESACTIVAR FACEBOOK PIXEL
  deactivatePixel() {
    try {
      console.log('🚫 Desactivando Facebook Pixel...');
      
      // Limpiar cookies de Facebook
      this.clearFacebookCookies();
      
      // Desactivar tracking
      if (window.fbq) {
        // No hay método oficial para "desactivar", pero podemos sobrescribir
        window.fbq = function() {
          console.log('🚫 Facebook Pixel desactivado - evento bloqueado:', arguments);
        };
      }
      
      this.isPixelActive = false;
      console.log('✅ Facebook Pixel desactivado');

    } catch (error) {
      console.error('❌ Error al desactivar Facebook Pixel:', error);
    }
  }

  // 📜 CARGAR SCRIPT DE FACEBOOK PIXEL
  // 📜 CARGAR SCRIPT DE FACEBOOK PIXEL
loadPixelScript() {
  try {
    if (this.isPixelLoaded || document.querySelector('script[src*="connect.facebook.net"]')) {
      console.log('ℹ️ Script de Facebook Pixel ya está cargado');
      return;
    }

    console.log('📜 Cargando script de Facebook Pixel...');

    // Crear función fbq si no existe
    if (!window.fbq) {
      window.fbq = function() {
        if (window.fbq.callMethod) {
          window.fbq.callMethod.apply(window.fbq, arguments);
        } else {
          window.fbq.queue.push(arguments);
        }
      };
      
      if (!window._fbq) {
        window._fbq = window.fbq;
      }
      
      window.fbq.push = window.fbq;
      window.fbq.loaded = true;
      window.fbq.version = '2.0';
      window.fbq.queue = [];
    }

    // Crear y cargar el script
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    
    // Insertar en el DOM
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }

    this.isPixelLoaded = true;
    console.log('✅ Script de Facebook Pixel cargado');

  } catch (error) {
    console.error('❌ Error al cargar script de Facebook Pixel:', error);
  }
}

  // 🧹 LIMPIAR COOKIES DE FACEBOOK
  clearFacebookCookies() {
    try {
      const facebookCookies = ['_fbp', '_fbc', 'fbm_', 'fbsr_'];
      
      for (const cookieName of facebookCookies) {
        // Limpiar cookies en diferentes dominios
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
        
        // Obtener dominio base (ej: ejemplo.com desde www.ejemplo.com)
        const domainParts = window.location.hostname.split('.');
        if (domainParts.length > 1) {
          const baseDomain = domainParts.slice(-2).join('.');
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${baseDomain}`;
        }
      }

      console.log('🧹 Cookies de Facebook limpiadas');
    } catch (error) {
      console.error('❌ Error al limpiar cookies de Facebook:', error);
    }
  }

  // 🔄 REVOCAR CONSENTIMIENTO
  async revokeConsent() {
    try {
      console.log('🔄 Revocando consentimiento de cookies...');
      
      // Notificar al backend ANTES de limpiar localStorage
      await this.sendRevocationToBackend();
      
      // Limpiar localStorage
      localStorage.removeItem('nexusai-cookie-preferences');
      localStorage.removeItem('nexusai-consent-given');
      localStorage.removeItem('nexusai-consent-timestamp');
      localStorage.removeItem('nexusai-cookie-user-id');
      
      // Desactivar servicios
      this.deactivatePixel();
      this.consentGiven = false;
      
      console.log('✅ Consentimiento revocado exitosamente');
      
      // Recargar página para mostrar banner nuevamente
      setTimeout(() => {
        window.location.reload();
      }, 1000);

    } catch (error) {
      console.error('❌ Error al revocar consentimiento:', error);
    }
  }

  // 🌐 NOTIFICAR REVOCACIÓN AL BACKEND
  async sendRevocationToBackend() {
    try {
      const userId = localStorage.getItem('nexusai-cookie-user-id');
      if (!userId) {
        console.log('ℹ️ No hay user_id para revocar');
        return;
      }

      const token = localStorage.getItem('nexusai-token');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${this.API_BASE_URL}/cookie-consent/revoke`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          user_id: userId,
          revocation_reason: 'user_request'
        })
      });

      if (response.ok) {
        console.log('✅ Revocación notificada al backend');
      } else {
        console.warn('⚠️ Error al notificar revocación al backend:', response.status);
      }
    } catch (error) {
      console.error('❌ Error al notificar revocación:', error);
    }
  }

  // 📊 OBTENER ESTADO ACTUAL
  getStatus() {
    return {
      consentGiven: this.consentGiven,
      isPixelActive: this.isPixelActive,
      isPixelLoaded: this.isPixelLoaded,
      preferences: this.getStoredConsent(),
      needsConsent: this.needsConsent(),
      userId: localStorage.getItem('nexusai-cookie-user-id'),
      sessionId: localStorage.getItem('nexusai-session-id')
    };
  }

  // 🔧 CONFIGURAR URL BASE DE LA API (útil para desarrollo/producción)
  setApiBaseUrl(url) {
    this.API_BASE_URL = url.replace(/\/$/, ''); // Remover slash final
    console.log('🔧 API Base URL configurada:', this.API_BASE_URL);
  }
}

// 🌍 CREAR INSTANCIA GLOBAL
const cookieService = new CookieService();

// 🚀 EXPORTAR PARA USO EN MÓDULOS
if (typeof module !== 'undefined' && module.exports) {
  module.exports = cookieService;
}

// 🌐 HACER DISPONIBLE GLOBALMENTE
if (typeof window !== 'undefined') {
  window.cookieService = cookieService;
}

export default cookieService;